/**
 * Shared formatting utilities for the GetClearance UI.
 */

/**
 * Format snake_case or UPPER_SNAKE_CASE to Title Case.
 * @param {string} str - Input string (e.g., "biometrics_failed" or "BIOMETRICS_FAILED")
 * @returns {string} Formatted string (e.g., "Biometrics Failed")
 */
export function formatEnumLabel(str) {
  if (!str) return '';
  return str
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Generate a short reference ID from a UUID.
 * @param {string} uuid - Full UUID
 * @param {string} prefix - Prefix like "APP"
 * @returns {string} Short reference like "APP-1A2B3C4D"
 */
export function shortRef(uuid, prefix = 'APP') {
  if (!uuid) return '';
  return `${prefix}-${uuid.slice(0, 8).toUpperCase()}`;
}

/**
 * Get initials from first/last name.
 * @param {string} firstName
 * @param {string} lastName
 * @returns {string} Initials like "RA"
 */
export function getInitials(firstName, lastName) {
  const f = (firstName || '')[0] || '';
  const l = (lastName || '')[0] || '';
  return (f + l).toUpperCase() || '?';
}

/**
 * Get a deterministic avatar color from an ID string.
 * Uses teal/cyan primary colors matching the TrustGate brand.
 * @param {string} id - UUID or any string
 * @returns {string} Hex color
 */
export function getAvatarColor(id) {
  // Teal/cyan focused palette matching TrustGate brand
  const colors = [
    '#14B8A6', // teal-500 (primary brand)
    '#06B6D4', // cyan-500
    '#0891B2', // cyan-600
    '#10B981', // emerald-500
    '#0D9488', // teal-600
    '#22D3EE', // cyan-400
    '#2DD4BF', // teal-400
    '#059669', // emerald-600
  ];
  if (!id) return colors[0];
  const hash = id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return colors[hash % colors.length];
}

/**
 * Screening list source ID to human-readable display name.
 * Covers OpenSanctions datasets and other common screening lists.
 */
const SCREENING_LIST_DISPLAY_NAMES = {
  // OFAC (US)
  us_ofac_sdn: 'OFAC SDN List',
  us_ofac_cons: 'OFAC Consolidated List',
  us_ofac_sdn_dates: 'OFAC SDN List',
  ofac_sdn: 'OFAC SDN List',

  // US Enforcement
  us_cftc_enforcement_actions: 'US CFTC Enforcement',
  us_sec_enforcement: 'US SEC Enforcement',
  us_doj_fugitives: 'US DOJ Fugitives',
  us_bis_denied: 'US BIS Denied Persons',
  us_occ_actions: 'US OCC Enforcement',
  us_fincen_actions: 'US FinCEN Actions',
  us_trade_csl: 'US Consolidated Screening',

  // EU
  eu_fsf: 'EU Financial Sanctions',
  eu_cor_members: 'EU Committee of Regions',
  eu_meps: 'EU Parliament Members',
  eu_sanctions: 'EU Sanctions List',

  // UN
  un_sc_sanctions: 'UN Security Council Sanctions',

  // UK
  gb_hmt_sanctions: 'UK HM Treasury Sanctions',
  gb_coh_disqualified: 'UK Disqualified Directors',
  gb_fca_prohibited: 'UK FCA Prohibited',

  // Other major lists
  interpol_red_notices: 'Interpol Red Notices',
  interpol_yellow_notices: 'Interpol Yellow Notices',
  worldbank_debarred: 'World Bank Debarred',
  imf_sanctions: 'IMF Sanctions',

  // OpenSanctions aggregates
  opensanctions: 'OpenSanctions',
  default: 'OpenSanctions Default',
  pep: 'Politically Exposed Persons',
  crime: 'Crime Database',
  debarment: 'Debarment List',

  // Hit types (fallback)
  sanctions: 'Sanctions List',
  adverse_media: 'Adverse Media',

  // Other regional
  au_dfat_sanctions: 'Australia DFAT Sanctions',
  ca_dfatd_sema_sanctions: 'Canada SEMA Sanctions',
  ch_seco_sanctions: 'Switzerland SECO Sanctions',
  jp_mof_sanctions: 'Japan MOF Sanctions',
  ru_nsd_isin: 'Russia NSD ISIN',
  cn_sanctions: 'China Sanctions',

  // Crime/enforcement
  fbi_most_wanted: 'FBI Most Wanted',
  fbi_terrorism: 'FBI Terrorism Watch',
  europol_wanted: 'Europol Most Wanted',

  // Chainalysis (crypto)
  chainalysis: 'Chainalysis Sanctions',
  chainalysis_sanctions: 'Chainalysis Sanctions Oracle',

  // PEP sources
  every_politician: 'Every Politician',
  wikidata_peps: 'Wikidata PEPs',
};

/**
 * Common acronyms that should remain uppercase in formatted names.
 */
const UPPERCASE_ACRONYMS = [
  'US', 'UK', 'EU', 'UN', 'UAE', 'AU', 'CA', 'JP', 'CH', 'RU', 'CN',
  'OFAC', 'SDN', 'CFTC', 'SEC', 'DOJ', 'FBI', 'CIA', 'BIS', 'OCC',
  'HMT', 'FCA', 'DFAT', 'SEMA', 'SECO', 'MOF', 'NSD', 'ISIN',
  'AML', 'KYC', 'PEP', 'IMF', 'FSF', 'CSL',
  'FINCEN', 'FATF',
];

/**
 * Convert snake_case to Title Case, preserving known acronyms.
 * @param {string} str - Input string (e.g., "us_cftc_enforcement_actions")
 * @returns {string} Formatted string (e.g., "US CFTC Enforcement Actions")
 */
function formatWithAcronyms(str) {
  if (!str) return '';

  // Split on underscores, format each word
  return str
    .split('_')
    .map(word => {
      const upper = word.toUpperCase();
      // Keep acronyms uppercase, title-case others
      if (UPPERCASE_ACRONYMS.includes(upper)) {
        return upper;
      }
      // Title case: first letter upper, rest lower
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Get human-readable display name for a screening list source.
 * @param {string} listSource - List source ID (e.g., "us_ofac_sdn")
 * @returns {string} Display name (e.g., "OFAC SDN List")
 */
export function formatListSource(listSource) {
  if (!listSource) return 'Unknown List';

  // Check exact match first
  if (SCREENING_LIST_DISPLAY_NAMES[listSource]) {
    return SCREENING_LIST_DISPLAY_NAMES[listSource];
  }

  // Check for partial matches (e.g., "ofac" in source)
  const lower = listSource.toLowerCase();
  if (lower.includes('ofac')) return 'OFAC Sanctions';
  if (lower.includes('eu_') || lower.includes('eu-')) return 'EU Sanctions';
  if (lower.includes('un_') || lower.includes('un-')) return 'UN Sanctions';
  if (lower.includes('pep')) return 'Politically Exposed Person';
  if (lower.includes('interpol')) return 'Interpol';
  if (lower.includes('sanction')) return 'Sanctions List';

  // Fallback: convert snake_case to Title Case with acronym preservation
  return formatWithAcronyms(listSource);
}
